import {
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User,
} from 'firebase/auth';
import { auth, googleProvider, githubProvider, appleProvider } from './firebase';
import { isApplePlatform } from './native/bridge';
import { askPrompt } from './dialogs';
import { t } from '../i18n';

export type { User };

/** A trimmed, serializable view of the signed-in user for the store/UI. */
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

export function toAuthUser(u: User | null): AuthUser | null {
  return u
    ? { uid: u.uid, email: u.email, displayName: u.displayName, photoURL: u.photoURL }
    : null;
}

/** Subscribe to auth state; returns an unsubscribe function. */
export function watchAuth(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, cb);
}

/**
 * Firebase ID token for the current user, or null if signed out. Used to
 * authenticate owner-scoped API calls (edit shares, share management).
 */
export async function getIdToken(): Promise<string | null> {
  const u = auth.currentUser;
  return u ? u.getIdToken() : null;
}

export const signInGoogle = (): Promise<unknown> => signInWithPopup(auth, googleProvider);
export const signInGithub = (): Promise<unknown> => signInWithPopup(auth, githubProvider);
export const signInApple = (): Promise<unknown> => signInWithPopup(auth, appleProvider);

/**
 * Whether to offer Sign in with Apple.
 *
 * On an Apple platform it is not optional: an app that offers Google or GitHub
 * sign-in must offer Apple's too (App Store guideline 4.8), so the button is
 * there whether or not the build was configured for it — a missing provider is
 * a setup error to see, not to hide. Elsewhere it appears once the Firebase
 * project has the provider enabled, which the build says with
 * `VITE_APPLE_SIGNIN=1`.
 */
export function appleSignInOffered(): boolean {
  return isApplePlatform() || import.meta.env.VITE_APPLE_SIGNIN === '1';
}
export const signOutUser = (): Promise<void> => fbSignOut(auth);

const EMAIL_KEY = 'markbird.emailForSignIn';

/** Send a passwordless magic-link to `email`; completes on return to the app. */
export async function sendEmailLink(email: string): Promise<void> {
  await sendSignInLinkToEmail(auth, email, {
    url: `${window.location.origin}/`,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(EMAIL_KEY, email);
}

/**
 * If the app was opened via an email sign-in link, finish the sign-in. Call on
 * load. Returns true if a sign-in completed.
 */
export async function completeEmailLinkIfPresent(): Promise<boolean> {
  if (!isSignInWithEmailLink(auth, window.location.href)) return false;
  let email = window.localStorage.getItem(EMAIL_KEY);
  // Only reached when the link is opened on a different device/browser than the
  // one that requested it, so localStorage has no address to confirm against.
  if (!email) email = (await askPrompt({ title: t('auth.confirmEmail') })) ?? '';
  if (!email) return false;
  await signInWithEmailLink(auth, email, window.location.href);
  window.localStorage.removeItem(EMAIL_KEY);
  window.history.replaceState({}, '', '/');
  return true;
}
